import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, JSX, Method, Prop, State, Watch } from '@stencil/core';
import Plyr from 'plyr';
import { WorkbenchRuntimeError } from '../../shared/errors';
import { CLASSES, DEFAULT_CONTROLS, MP4_REG_EX, VIMEO_REG_EX, WEBM_REG_EX, YOUTUBE_REG_EX } from './video.constants';
import { VideoStatus } from './video.types';
import { bindPlayer } from './player/player';
import { bindControls } from './controls/controls';

@Component({
  tag: 'wb-video',
  styleUrl: 'video.scss',
  shadow: true,
})
export class VideoComponent implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /** Source of the video */
  @Prop()
  public src!: string;

  /** aria-label for play button */
  @Prop()
  public playAriaLabel!: string;

  /** aria-label for pause button */
  @Prop()
  public pauseAriaLabel!: string;

  /** Source of the captions, if provided the video will render the captions */
  @Prop()
  public captions: string;

  /** Sets video as autoplay */
  @Prop()
  public autoplay: boolean = false;

  /** Source of the preview thumbnails which will be shown when hovering over the time slider  */
  @Prop()
  public thumbnailUrl: string;

  /** Source of the poster image */
  @Prop()
  public poster: string;

  /** Controls and the order in which they are displayed in the player */
  @Prop()
  public controls: string = DEFAULT_CONTROLS;

  /** Defines the starting volume of the video */
  @Prop({ mutable: true })
  public volume: number = 1;

  /** Time (in seconds) of the video. Can be used initially or during component runtime */
  @Prop({ reflect: true })
  public time: number = 0;

  @State()
  private status: VideoStatus = 'paused';

  /** Triggers fullscreen mode */
  @Method()
  public async toggleFullscreen(): Promise<void> {
    this.plyrInstance?.fullscreen?.toggle();
  }

  /** Toggle the video play / pause */
  @Method()
  public async togglePlay(): Promise<void> {
    this.plyrInstance?.togglePlay();
  }

  /** Pauses the video */
  @Method()
  public async pause(): Promise<void> {
    this.plyrInstance?.pause();
  }

  /** Plays the video */
  @Method()
  public async play(): Promise<void> {
    this.plyrInstance?.play();
  }

  /** Stops the video */
  @Method()
  public async stop(): Promise<void> {
    this.plyrInstance?.stop();
  }

  /** Enters fullscreen video */
  @Method()
  public async enterFullscreen(): Promise<void> {
    this.plyrInstance?.fullscreen?.enter();
  }

  /** Exists fullscreen video */
  @Method()
  public async exitFullscreen(): Promise<void> {
    this.plyrInstance?.fullscreen?.exit();
  }

  @Watch('time')
  public onTimeChange(): void {
    this.plyrInstance.currentTime = this.time;
  }

  /** Emitted when playback is ready; that is, when playback is resumed after a prior pause event */
  @Event()
  public wbready: EventEmitter;

  /** Emitted when playback of the media starts after having been paused; that is, when playback is resumed after a prior pause event */
  @Event()
  public wbplay: EventEmitter;

  /** Emitted when the media begins to play (either for the first time, after having been paused, or after ending and then restarting). */
  @Event()
  public wbplaying: EventEmitter;

  /** Emitted when playback is paused. */
  @Event()
  public wbpause: EventEmitter;

  /** Emitted when playback completes. */
  @Event()
  public wbended: EventEmitter;

  /** Emitted when a certain portion of the video completed */
  @Event()
  public wbcompletedpercent: EventEmitter<number>;

  private playInterval: ReturnType<typeof setInterval>;
  private intervalMs: number = 500;
  private eventEmitted: boolean = false;
  private videoEl: HTMLDivElement | HTMLVideoElement;
  private plyrInstance: Plyr;

  @Watch('src')
  public onSrcChange(): void {
    if (this.src.match(YOUTUBE_REG_EX)) {
      this.plyrInstance.source = {
        type: 'video',
        sources: [{ src: this.src, provider: 'youtube' }],
      };
    } else if (this.src.match(VIMEO_REG_EX)) {
      this.plyrInstance.source = {
        type: 'video',
        sources: [{ src: this.src, provider: 'vimeo' }],
      };
    } else {
      this.plyrInstance.source = {
        type: 'video',
        sources: [{ src: this.src, type: this.getVideoType(this.src) }],
      };
    }
  }

  @Watch('poster')
  public onPosterChange(): void {
    this.plyrInstance.poster = this.poster;
  }

  public componentDidLoad(): void {
    this.initializePlyr();
  }

  public disconnectedCallback(): void {
    this.plyrInstance.destroy();
  }

  private initializePlyr(): void {
    const controls = this.getControls();

    this.plyrInstance = new Plyr(this.videoEl, {
      autoplay: this.autoplay,
      clickToPlay: !this.autoplay,
      controls,
      hideControls: false,
      loop: { active: this.autoplay },
      volume: this.volume,
      loadSprite: false,
      previewThumbnails: {
        enabled: Boolean(this.thumbnailUrl),
        src: this.thumbnailUrl || '',
      },
    });

    this.plyrInstance.poster = this.poster;

    this.plyrInstance.on('ready', (e) => {
      this.onPlayerReady();
      this.plyrInstance.currentTime = this.time; // Initialize video with time value
      this.wbready.emit(e);
    });

    this.plyrInstance.on('loadeddata', () => {
      this.plyrInstance.currentTime = this.time; // Required for MP4 to initialize video with time value
    });

    this.plyrInstance.on('play', () => {
      if (this.plyrInstance.provider === 'vimeo') {
        this.plyrInstance.currentTime = this.time; // Required for Vimeo to initialize video with time value
      }
      this.wbplay.emit();
      this.playInterval = setInterval(this.calcPercentagePlayed, this.intervalMs);
    });

    // TODO: this event is triggered every few miliseconds. not ideal. we should update this
    // https://git.i.mercedes-benz.com/dh-io-workbench/core/issues/2852
    this.plyrInstance.on('playing', () => {
      this.wbplaying.emit();
      this.status = 'playing';
    });

    this.plyrInstance.on('pause', () => {
      this.wbpause.emit();
      this.status = 'paused';
      if (this.playInterval) {
        clearInterval(this.playInterval);
      }
    });

    this.plyrInstance.on('ended', () => {
      this.wbended.emit();
      this.status = 'ended';
    });
  }

  private onPlayerReady = (): void => {
    const volumeButton = this.el.shadowRoot.querySelector(`.${CLASSES.plyrVolume} button`);
    const volumeRange = this.el.shadowRoot.querySelector(`.${CLASSES.plyrVolume} input`);
    this.plyrInstance.volume = this.volume; // required for embedded players (i.e. youtube)

    // volume button is added via controls and can be unavailable
    if (volumeButton && volumeRange) {
      volumeButton.addEventListener('click', () => {
        volumeRange.classList.toggle(CLASSES.isVisible);
        volumeButton.classList.toggle(CLASSES.plyrControlActive);
      });

      document.addEventListener('click', (clickEvent) => {
        const target = clickEvent.target;
        const volumeControl = this.el.shadowRoot.querySelector(`.${CLASSES.plyrVolume}`);

        if (target !== volumeControl && target !== volumeButton && target !== volumeRange) {
          volumeRange.classList.remove(CLASSES.isVisible);
          volumeButton.classList.remove(CLASSES.plyrControlActive);
        }
      });
    }
  };

  private getControls = (): string => {
    const template = this.el.shadowRoot.querySelector('#controls') as HTMLTemplateElement;
    const div = document.createElement('div');
    template.childNodes.forEach((node) => {
      div.appendChild(node);
    });
    return div.innerHTML;
  };

  private getVideoType(src: string): string {
    if (src.match(MP4_REG_EX)) {
      return 'video/mp4';
    }

    if (src.match(WEBM_REG_EX)) {
      return 'video/webm';
    }

    throw new WorkbenchRuntimeError('invalid video format. supports only mp4 and webm', 'wb-video');
  }

  private bindVideoElement = (el: HTMLDivElement | HTMLVideoElement): void => {
    this.videoEl = el;
  };

  private bindTogglePlay = (): void => {
    this.togglePlay();
  };

  private calcPercentagePlayed = (): void => {
    const percentagePlayed = Math.round((this.plyrInstance.currentTime / this.plyrInstance.duration) * 100);
    if (percentagePlayed == 25 || percentagePlayed == 50 || percentagePlayed == 75 || percentagePlayed == 90) {
      if (!this.eventEmitted) {
        this.eventEmitted = true;
        this.wbcompletedpercent.emit(percentagePlayed);
      }
    } else {
      if (this.eventEmitted) {
        this.eventEmitted = false;
      }
    }
  };

  public render(): JSX.Element {
    const Player = bindPlayer(this);
    const Controls = bindControls(this);

    return (
      <Host class="wb-video">
        <Player src={this.src} captions={this.captions} autoplay={this.autoplay} status={this.status} togglePlay={this.bindTogglePlay} bindVideoElement={this.bindVideoElement} getVideoType={this.getVideoType} playAriaLabel={this.playAriaLabel} pauseAriaLabel={this.pauseAriaLabel} />
        <Controls controls={this.controls} />
      </Host>
    );
  }
}
